import iconCopy from '@phosphor-icons/core/regular/copy.svg';
import React, { useState } from 'react';
import { FormattedDate, FormattedMessage, defineMessages, useIntl } from 'react-intl';

import CopyableInput from '@/components/copyable-input';
import ScrollableList from '@/components/scrollable-list';
import Column from '@/components/ui/column';
import Form from '@/components/ui/form';
import Icon from '@/components/ui/icon';
import Input from '@/components/ui/input';
import { useFeatures } from '@/hooks/use-features';
import {
  useInvites,
  useCreateInviteTokenMutation,
  useRevokeInviteTokenMutation,
  useEmailInviteMutation,
} from '@/queries/admin/use-invites';
import { useModalsActions } from '@/stores/modals';
import toast from '@/toast';
import copy from '@/utils/copy';

import type { AdminInvite } from 'pl-api';

const messages = defineMessages({
  heading: { id: 'column.admin.invites', defaultMessage: 'Invites' },
  maxUsePlaceholder: {
    id: 'admin.invites.max_use.placeholder',
    defaultMessage: 'Max uses (optional)',
  },
  expiresPlaceholder: {
    id: 'admin.invites.expires_at.placeholder',
    defaultMessage: 'Expiry date (optional)',
  },
  emailPlaceholder: { id: 'admin.invites.email.placeholder', defaultMessage: 'E-mail address' },
  namePlaceholder: { id: 'admin.invites.name.placeholder', defaultMessage: 'Name (optional)' },
  createSuccess: { id: 'admin.invites.create.success', defaultMessage: 'Invite token created' },
  createError: {
    id: 'admin.invites.create.error',
    defaultMessage: 'Failed to create invite token',
  },
  emailSuccess: { id: 'admin.invites.email.success', defaultMessage: 'Invite e-mail sent' },
  emailError: { id: 'admin.invites.email.error', defaultMessage: 'Failed to send invite e-mail' },
  emailErrorInvitesDisabled: {
    id: 'admin.invites.email.error.invites_disabled',
    defaultMessage: 'You need to enable user invitations for admins to use this feature',
  },
  revokeSuccess: { id: 'admin.invites.revoke.success', defaultMessage: 'Invite token revoked' },
  revokeError: {
    id: 'admin.invites.revoke.error',
    defaultMessage: 'Failed to revoke invite token',
  },
  revokeConfirm: { id: 'confirmations.admin.revoke_invite.confirm', defaultMessage: 'Revoke' },
  copied: { id: 'admin.invites.copied', defaultMessage: 'Token copied to clipboard' },
  tokenPlaceholder: {
    id: 'admin.invites.revoked_token.placeholder',
    defaultMessage: 'Enter token to revoke…',
  },
  generatedTokenPlaceholder: {
    id: 'admin.invites.generated_token.placeholder',
    defaultMessage: 'Generated token will appear here',
  },
});

interface IInvite {
  invite: AdminInvite;
}

const Invite: React.FC<IInvite> = ({ invite }) => {
  const intl = useIntl();

  const { openModal } = useModalsActions();
  const { mutate } = useRevokeInviteTokenMutation();

  const handleCopy = () => {
    copy(invite.token, () => toast.success(messages.copied));
  };

  const handleRevoke = () => {
    openModal('CONFIRM', {
      heading: (
        <FormattedMessage
          id='confirmations.admin.revoke_invite.heading'
          defaultMessage='Revoke invite token'
        />
      ),
      message: (
        <FormattedMessage
          id='confirmations.admin.revoke_invite.message'
          defaultMessage='Are you sure you want to revoke this invite token?'
        />
      ),
      confirm: intl.formatMessage(messages.revokeConfirm),
      onConfirm: () => {
        mutate(invite.token, {
          onSuccess: () => toast.success(messages.revokeSuccess),
          onError: () => toast.error(messages.revokeError),
        });
      },
    });
  };

  return (
    <div className='admin-invite'>
      <div className='admin-invite__token'>
        <code>{invite.token}</code>
        <button
          type='button'
          className='admin-invite__copy'
          onClick={handleCopy}
          title={intl.formatMessage(messages.copied)}
        >
          <Icon src={iconCopy} />
        </button>
      </div>

      <div className='admin-invite__meta'>
        <span>
          <FormattedMessage
            id='admin.invites.uses'
            defaultMessage='Uses: {uses}{maxUse, select, none {} other { / {maxUse}}}'
            values={{
              uses: invite.uses,
              maxUse: invite.max_use ?? 'none',
            }}
          />
        </span>

        {invite.expires_at && (
          <span>
            <FormattedMessage id='admin.invites.expires' defaultMessage='Expires:' />{' '}
            <FormattedDate value={invite.expires_at} year='numeric' month='short' day='2-digit' />
          </span>
        )}

        {invite.used && (
          <span className='admin-invite__used'>
            <FormattedMessage id='admin.invites.used' defaultMessage='Used' />
          </span>
        )}
      </div>

      <div className='admin-invite__actions'>
        <button type='button' disabled={invite.used} onClick={handleRevoke}>
          <FormattedMessage id='admin.invites.revoke' defaultMessage='Revoke' />
        </button>
      </div>
    </div>
  );
};

const NewInviteForm: React.FC = () => {
  const intl = useIntl();
  const features = useFeatures();

  const [maxUse, setMaxUse] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [token, setToken] = useState('');

  const { mutate, isPending } = useCreateInviteTokenMutation();

  const handleSubmit: React.SubmitEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    mutate(
      {
        max_use: maxUse ? Number(maxUse) : undefined,
        expires_at: expiresAt || undefined,
      },
      {
        onSuccess: (data) => {
          setMaxUse('');
          setExpiresAt('');
          toast.success(messages.createSuccess);
          setToken(data.token);
        },
        onError: () => toast.error(messages.createError),
      },
    );
  };

  return (
    <Form onSubmit={handleSubmit} className='admin-invites-page__form'>
      {features.pleromaAdminInvites ? (
        <>
          <Input
            type='number'
            min={1}
            placeholder={intl.formatMessage(messages.maxUsePlaceholder)}
            value={maxUse}
            onChange={(e) => setMaxUse(e.target.value)}
          />
          <Input
            type='date'
            placeholder={intl.formatMessage(messages.expiresPlaceholder)}
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </>
      ) : (
        <CopyableInput
          placeholder={intl.formatMessage(messages.generatedTokenPlaceholder)}
          value={token}
          disabled={!token}
        />
      )}
      <button type='submit' disabled={isPending}>
        <FormattedMessage id='admin.invites.create' defaultMessage='Generate token' />
      </button>
    </Form>
  );
};

const EmailInviteForm: React.FC = () => {
  const intl = useIntl();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const { mutate, isPending } = useEmailInviteMutation();

  const handleSubmit: React.SubmitEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    mutate(
      { email, name: name || undefined },
      {
        onSuccess: () => {
          setEmail('');
          setName('');
          toast.success(messages.emailSuccess);
        },
        onError: (error: any) => {
          if (error.response?.json?.error?.includes('invites_enabled')) {
            toast.error(messages.emailErrorInvitesDisabled);
          } else {
            toast.error(messages.emailError);
          }
        },
      },
    );
  };

  return (
    <Form onSubmit={handleSubmit} className='admin-invites-page__form'>
      <Input
        type='email'
        required
        placeholder={intl.formatMessage(messages.emailPlaceholder)}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Input
        type='text'
        placeholder={intl.formatMessage(messages.namePlaceholder)}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <button type='submit' disabled={isPending || !email}>
        <FormattedMessage id='admin.invites.email.send' defaultMessage='Send e-mail invite' />
      </button>
    </Form>
  );
};

const RevokeInviteForm: React.FC = () => {
  const intl = useIntl();
  const [token, setToken] = useState('');

  const { mutate, isPending } = useRevokeInviteTokenMutation();

  const handleSubmit: React.SubmitEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    mutate(token, {
      onSuccess: () => {
        toast.success(messages.revokeSuccess);
        setToken('');
      },
      onError: () => toast.error(messages.revokeError),
    });
  };

  return (
    <Form onSubmit={handleSubmit} className='admin-invites-page__form'>
      <Input
        required
        placeholder={intl.formatMessage(messages.tokenPlaceholder)}
        value={token}
        onChange={(e) => setToken(e.target.value)}
      />
      <button type='submit' disabled={!token || isPending}>
        <FormattedMessage id='admin.invites.revoke.action' defaultMessage='Revoke invite' />
      </button>
    </Form>
  );
};

const InvitesPage: React.FC = () => {
  const intl = useIntl();
  const features = useFeatures();

  const { data: invites, isFetching } = useInvites(features.pleromaAdminInvites);

  const emptyMessage = (
    <FormattedMessage
      id='empty_column.admin.invites'
      defaultMessage='There are no invite tokens yet.'
    />
  );

  return (
    <Column label={intl.formatMessage(messages.heading)}>
      <div className='admin-invites-page'>
        <NewInviteForm />
        {features.pleromaAdminInvites && <EmailInviteForm />}
        {features.iceshrimpAdmin && <RevokeInviteForm />}

        {invites && (
          <ScrollableList
            scrollKey='invites'
            emptyMessageText={emptyMessage}
            itemClassName='admin-invite__container'
            isLoading={isFetching}
            showLoading={isFetching && !invites?.length}
          >
            {invites.map((invite) => (
              <Invite key={invite.token} invite={invite} />
            ))}
          </ScrollableList>
        )}
      </div>
    </Column>
  );
};

export { InvitesPage as default };
